import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormError } from '@/components/FormError'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import type { Family } from '@/lib/families'
import { firstName } from '@/lib/tree'

// Who came in through a link you made. A link works for whoever opens it first, so you get one chance to say it
// reached the wrong person, which takes them back out. Saying it's them puts the note away.
export function FromLinks({ family }: { family: Family }) {
  if (family.fromLinks.length === 0) return null
  return (
    <section aria-label="Joined from your links" className="flex flex-col gap-3">
      {family.fromLinks.map((x) => (
        <Joined key={x.inviteId} familyId={family.id} joined={x} />
      ))}
    </section>
  )
}

function Joined({ familyId, joined }: { familyId: string; joined: Family['fromLinks'][number] }) {
  const queryClient = useQueryClient()
  const check = useMutation({
    mutationFn: (theyreRight: boolean) =>
      api(`/families/${familyId}/invites/${joined.inviteId}/check`, { method: 'POST', body: { theyreRight } }),
    onSuccess: () =>
      Promise.all(
        [['family', familyId], ['tree', familyId], ['circles'], ['framily']].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      ),
  })
  const meant = joined.invitedAs ? firstName(joined.invitedAs) : 'them'
  return (
    <div className="flex flex-col gap-3 rounded-[1.25rem] border p-4">
      <p>
        <span className="font-semibold">{joined.name} joined</span>
        <span className="text-muted-foreground">
          {' '}
          from the link you made{joined.invitedAs ? ` for ${joined.invitedAs}` : ''}.
        </span>
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={check.isPending} onClick={() => check.mutate(true)}>
          That’s {meant}
        </Button>
        <Button size="sm" variant="outline" disabled={check.isPending} onClick={() => check.mutate(false)}>
          That’s not {meant}
        </Button>
      </div>
      <FormError error={check.error} />
    </div>
  )
}
